import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { QRCodeSVG } from "qrcode.react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Copy, Link2, MousePointerClick } from "lucide-react";

export default function SignalLinkHub({ vessel }) {
  const [links, setLinks] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ label: "", target_url: "", campaign: "" });

  const load = async () => {
    const r = await api.get("/links");
    setLinks(r.data);
  };
  useEffect(() => { load(); }, []);

  const visible = useMemo(() => links.filter((l) => l.vessel === vessel), [links, vessel]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/links", { ...form, vessel });
      setForm({ label: "", target_url: "", campaign: "" });
      setShowForm(false);
      toast.success("Signal beacon broadcast.");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };
  const remove = async (id) => {
    await api.delete(`/links/${id}`);
    toast.success("Beacon retired.");
    load();
  };

  const shortUrl = (l) => `eig.oo/${l.short_code}`;

  const copy = async (txt) => {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(txt);
      } else {
        const ta = document.createElement("textarea");
        ta.value = txt;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.focus();
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      toast.success("Copied to clipboard.");
    } catch (e) {
      toast.error("Copy failed. Select & copy manually.");
    }
  };

  return (
    <div className="space-y-6" data-testid="signal-link-hub">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="label-mono text-[#D8AB4E]">MODULE 05 // SIGNAL-LINK</div>
          <h2 className="font-display text-3xl sm:text-4xl mt-1">Branded Beacon Gallery</h2>
          <p className="text-zinc-400 text-sm mt-2 max-w-xl">
            Every short-link, every QR — one sovereign control surface for the Link-Engine.
          </p>
        </div>
        <button data-testid="link-add-button" onClick={() => setShowForm((v) => !v)} className="btn-gold px-5 py-3 text-[11px] inline-flex items-center gap-2">
          <Plus size={14} /> {showForm ? "CLOSE" : "MINT BEACON"}
        </button>
      </div>

      {showForm && (
        <motion.form
          initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
          onSubmit={submit}
          className="glass-panel p-6 grid md:grid-cols-12 gap-4"
          data-testid="link-form"
        >
          <div className="md:col-span-3">
            <label className="label-mono text-zinc-400 mb-2 block">Label</label>
            <input
              data-testid="link-label-input"
              required
              value={form.label}
              onChange={(e) => setForm({ ...form, label: e.target.value })}
              className="w-full bg-[#0A0A0A] border border-[#D8AB4E]/30 px-3 py-2.5 text-sm focus:outline-none focus:border-[#D8AB4E]"
              placeholder="Q2 Memo"
            />
          </div>
          <div className="md:col-span-5">
            <label className="label-mono text-zinc-400 mb-2 block">Target URL</label>
            <input
              data-testid="link-url-input"
              required type="url"
              value={form.target_url}
              onChange={(e) => setForm({ ...form, target_url: e.target.value })}
              className="w-full bg-[#0A0A0A] border border-[#D8AB4E]/30 px-3 py-2.5 text-sm focus:outline-none focus:border-[#D8AB4E]"
              placeholder="https://trade.bizboogie.com/memo"
            />
          </div>
          <div className="md:col-span-2">
            <label className="label-mono text-zinc-400 mb-2 block">Campaign</label>
            <input
              data-testid="link-campaign-input"
              value={form.campaign}
              onChange={(e) => setForm({ ...form, campaign: e.target.value })}
              className="w-full bg-[#0A0A0A] border border-[#D8AB4E]/30 px-3 py-2.5 text-sm focus:outline-none focus:border-[#D8AB4E]"
              placeholder="Optional"
            />
          </div>
          <div className="md:col-span-2 flex items-end">
            <button data-testid="link-submit-button" className="btn-gold w-full py-2.5 text-[11px]">MINT</button>
          </div>
        </motion.form>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {visible.length === 0 && (
          <div className="md:col-span-3 glass-panel p-10 text-center text-zinc-500 font-mono-data text-sm">
            No beacons on this vessel yet. Mint your first signal.
          </div>
        )}
        {visible.map((l) => (
          <motion.div
            key={l.id}
            whileHover={{ y: -2 }}
            className="glass-panel p-5 relative grain"
            data-testid={`link-${l.id}`}
          >
            <div className="flex items-start gap-4">
              <div className="bg-white p-2 shrink-0">
                <QRCodeSVG
                  value={l.target_url}
                  size={72}
                  bgColor="#FFFFFF"
                  fgColor="#040404"
                  level="M"
                />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-display text-base truncate">{l.label}</div>
                <div className="label-mono text-zinc-500 mt-0.5">{l.campaign || "—"}</div>
                <div className="mt-3 font-mono-data text-xs text-[#D8AB4E] truncate">{shortUrl(l)}</div>
                <div className="font-mono-data text-[10px] text-zinc-500 truncate">{l.target_url}</div>
              </div>
            </div>
            <div className="mt-4 pt-4 border-t border-[#D8AB4E]/10 flex items-center justify-between">
              <div className="flex items-center gap-2 font-mono-data text-xs text-zinc-300">
                <MousePointerClick size={13} className="text-[#D8AB4E]" /> {l.clicks.toLocaleString()} clicks
              </div>
              <div className="flex items-center gap-1">
                <button
                  data-testid={`link-copy-${l.id}`}
                  onClick={() => copy(shortUrl(l))}
                  className="h-8 w-8 grid place-items-center border border-[#D8AB4E]/25 text-[#D8AB4E] hover:bg-[#D8AB4E]/10 transition-colors"
                  title="Copy short URL"
                >
                  <Copy size={12} />
                </button>
                <button
                  data-testid={`link-delete-${l.id}`}
                  onClick={() => remove(l.id)}
                  className="h-8 w-8 grid place-items-center border border-zinc-800 text-zinc-500 hover:text-[#E55A45] hover:border-[#C1432E]/50 transition-colors"
                >
                  <Trash2 size={12} />
                </button>
              </div>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
}
